import { useLayoutEffect, useRef, useState } from "react";
import { WizardDocNote } from "../../../types/api-types";
import formatDateString from "../../../utils/formatted-date";

// Styles
import styles from './info.module.css';

interface NotesCardProps {
  artNotes?: string;
  embroideryNotes?: EmbroideryNote;
  customerEmbroideryNote?: string;
  internalNotes?: WizardDocNote[];
  specialInstructions?: string;
  weiDesignNotes?: string;
  maxHeight: number;
}

interface NoteItemProps {
  label: string;
  content: string;
}

export interface EmbroideryNote {
  note: string | null;
  date: string | null;
  createdBy: string | null;
}

export const hasNonEmptyNote = (note: EmbroideryNote | undefined): boolean => {
  if (!note || !note.note) return false;
  return note.note.trim() !== '';
};

export const NotesCard = ({
  artNotes,
  embroideryNotes,
  customerEmbroideryNote,
  internalNotes,
  specialInstructions,
  weiDesignNotes,
  maxHeight
}: NotesCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => setIsExpanded(!isExpanded);
  const artNotesRef = useRef<HTMLDivElement>(null);
  const [artNotesHeight, setArtNotesHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (artNotesRef.current) {
      const spanHeight = artNotesRef.current.scrollHeight;
      setArtNotesHeight(spanHeight);
    }
  }, [artNotes, embroideryNotes, internalNotes, maxHeight, specialInstructions, weiDesignNotes]);

  return (
    <><div
      className={`${styles.artNotesContainer} ${isExpanded ? styles.artNotesExpanded : ''}`}
      style={{
        maxHeight: !isExpanded ? maxHeight : "",
        transition: 'max-height 0.3s ease'
      }}>
      <div className={styles.notesWrapper} ref={artNotesRef}>
        {artNotes && (
          <NoteItem label="Art Note" content={artNotes} />
        )}
        {customerEmbroideryNote && (
          <NoteItem label="Embroidery Note" content={customerEmbroideryNote} />
        )}
        {hasNonEmptyNote(embroideryNotes) &&   (
          <div className={styles.details}>
            <span className={styles.name}>Internal Embroidery Note:</span>
            <div className="flex flex-row items-center justify-start w-full mt-1 pl-2 gap-2">
              <span className="text-xs text-[gray]">{embroideryNotes?.date ? formatDateString({ dateString: embroideryNotes?.date }) : ""}</span>
              <span className="text-xs text-black font-medium">{embroideryNotes?.createdBy ? `by ${embroideryNotes.createdBy}` : <i>by unknown</i>}</span>
            </div>
            <p className={`${styles.value} pl-2`}>- {embroideryNotes?.note}</p>
          </div>
        )}
        {internalNotes && internalNotes?.length > 0 &&
          <div className={styles.details}>
            <span className={styles.name}>Internal Notes:</span>
            {internalNotes.map((note) => {
              const formattedDate = note?.createdDate ? formatDateString({ dateString: note?.createdDate }) : "";
              return (<div key={note.id} className="mb-2">
                <div className="flex flex-row items-center justify-start w-full mt-1 pl-2 gap-2">
                  {formattedDate && <span className="text-xs text-[gray]">{formattedDate}</span>}
                  <span className="text-xs text-black font-medium">{note.createdBy ? `by ${note.createdBy}` : <i>by unknown</i>}</span>
                </div>
                <p className={`${styles.value} pl-2`}>- {note.note}</p>
              </div>)
            })}
          </div>
        }
        {specialInstructions && (
          <NoteItem label="Special Instructions" content={specialInstructions} />
        )}
        {weiDesignNotes && (
          <NoteItem label="WEI Design Notes" content={weiDesignNotes} />
        )}
      </div>
    </div>
      <div>
        {((artNotesHeight + 15) > maxHeight) &&
          <div onClick={toggleExpand} className={styles.artNotesToggleButton}>
            <span>{isExpanded ? '...view less' : '...view more'}</span>
          </div>
        }
      </div>
    </>
  );
};

const NoteItem = ({ label, content }: NoteItemProps) => (
  <div className={styles.details}>
    <span className={styles.name}>{label}:</span>
    <p className={`${styles.value} pl-2`}>- {content}</p>
  </div>
);